import React, { useState, useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaHospital, FaMapMarkerAlt, FaPhone, FaEnvelope, FaIdCard, FaCalendarAlt, FaEdit, FaMap } from 'react-icons/fa';
import { showErrorToast } from '../../../shared/utils/toastHelper';
import { fetchClinicProfile, clearError } from '../../../redux/Slices/ClinicProfileSlice';
import { useNavigate } from 'react-router-dom';

const ClinicProfileView = () => {
    const { setTitle } = useTitle();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { profileData, loading, error } = useSelector((state) => state.clinicProfile);

    useEffect(() => {
        setTitle('الملف الشخصي للعيادة');
        dispatch(fetchClinicProfile());
    }, [setTitle, dispatch]);

    useEffect(() => {
        if (error) {
            showErrorToast(error);
            dispatch(clearError());
        }
    }, [error, dispatch]);

    if (loading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">جاري تحميل بيانات العيادة...</p>
                    </div>
                </div>
            </div>
        );
    }

    // البيانات تأتي مباشرة من API response
    const clinicData = profileData?.result || profileData;

    if (!clinicData) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <FaHospital className="text-muted" style={{ fontSize: '4rem' }} />
                        <h5 className="mt-3 text-muted">لا توجد بيانات للعيادة</h5>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">الملف الشخصي للعيادة</h4>
                    <motion.button
                        className="btn btn-primary"
                        onClick={() => navigate('/clinic/profile/edit')}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '10px 20px',
                            fontSize: '14px',
                            fontWeight: '600',
                            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                        }}
                    >
                        <FaEdit className="mx-2" />
                        تعديل البيانات
                    </motion.button>
                </div>

                {/* Profile Cards */}
                <div className="row">
                    {/* Basic Information */}
                    <div className="col-lg-8 mb-4">
                        <motion.div
                            className="card shadow-lg border-0 h-100"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            <div className="card-header bg-primary text-white">
                                <h5 className="mb-0">
                                    <FaHospital className="mx-2" />
                                    المعلومات الأساسية
                                </h5>
                            </div>
                            <div className="card-body p-4">
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold text-muted">اسم العيادة</label>
                                        <p className="form-control-plaintext fw-medium">{clinicData.title || 'غير محدد'}</p>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold text-muted">رقم الهاتف</label>
                                        <p className="form-control-plaintext fw-medium">
                                            <FaPhone className="mx-2 text-primary" />
                                            {clinicData.phone || 'غير محدد'}
                                        </p>
                                    </div>
                                    <div className="col-12 mb-3">
                                        <label className="form-label fw-semibold text-muted">الوصف</label>
                                        <p className="form-control-plaintext fw-medium">{clinicData.description || 'غير محدد'}</p>
                                    </div>
                                    <div className="col-12 mb-3">
                                        <label className="form-label fw-semibold text-muted">العنوان</label>
                                        <p className="form-control-plaintext fw-medium">
                                            <FaMapMarkerAlt className="mx-2 text-primary" />
                                            {clinicData.address || 'غير محدد'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    {/* Logo */}
                    <div className="col-lg-4 mb-4">
                        <motion.div
                            className="card shadow-lg border-0 h-100"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                        >
                            <div className="card-header bg-primary text-white">
                                <h5 className="mb-0">لوجو العيادة</h5>
                            </div>
                            <div className="card-body p-4 text-center">
                                {clinicData.logo ? (
                                    <img
                                        src={clinicData.logo}
                                        alt="لوجو العيادة"
                                        className="img-fluid rounded"
                                        style={{
                                            maxWidth: '200px',
                                            maxHeight: '200px',
                                            objectFit: 'cover',
                                            borderRadius: '12px'
                                        }}
                                    />
                                ) : (
                                    <div className="d-flex align-items-center justify-content-center" style={{ height: '200px' }}>
                                        <FaHospital className="text-muted" style={{ fontSize: '4rem' }} />
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </div>

                    {/* Cover Image */}
                    <div className="col-12 mb-4">
                        <motion.div
                            className="card shadow-lg border-0"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                        >
                            <div className="card-header bg-primary text-white">
                                <h5 className="mb-0">صورة الغلاف</h5>
                            </div>
                            <div className="card-body p-0">
                                {clinicData.cover ? (
                                    <img
                                        src={clinicData.cover}
                                        alt="صورة الغلاف"
                                        className="img-fluid w-100"
                                        style={{
                                            height: '300px',
                                            objectFit: 'cover'
                                        }}
                                    />
                                ) : (
                                    <div className="d-flex align-items-center justify-content-center" style={{ height: '300px' }}>
                                        <FaHospital className="text-muted" style={{ fontSize: '4rem' }} />
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </div>

                    {/* License Information */}
                    <div className="col-lg-6 mb-4">
                        <motion.div
                            className="card shadow-lg border-0 h-100"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                        >
                            <div className="card-header bg-primary text-white">
                                <h5 className="mb-0">
                                    <FaIdCard className="mx-2" />
                                    معلومات الترخيص
                                </h5>
                            </div>
                            <div className="card-body p-4">
                                <div className="row">
                                    <div className="col-12 mb-3">
                                        <label className="form-label fw-semibold text-muted">رقم الترخيص</label>
                                        <p className="form-control-plaintext fw-medium">{clinicData.license_number || 'غير محدد'}</p>
                                    </div>
                                    <div className="col-12 mb-3">
                                        <label className="form-label fw-semibold text-muted">تاريخ انتهاء الترخيص</label>
                                        <p className="form-control-plaintext fw-medium">
                                            <FaCalendarAlt className="mx-2 text-primary" />
                                            {clinicData.license_expiry || 'غير محدد'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    {/* Location */}
                    <div className="col-lg-6 mb-4">
                        <motion.div
                            className="card shadow-lg border-0 h-100"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.4 }}
                        >
                            <div className="card-header bg-primary text-white">
                                <h5 className="mb-0">
                                    <FaMap className="mx-2" />
                                    الموقع
                                </h5>
                            </div>
                            <div className="card-body p-4">
                                <div className="row">
                                    <div className="col-6 mb-3">
                                        <label className="form-label fw-semibold text-muted">خط العرض</label>
                                        <p className="form-control-plaintext fw-medium">{clinicData.lat || 'غير محدد'}</p>
                                    </div>
                                    <div className="col-6 mb-3">
                                        <label className="form-label fw-semibold text-muted">خط الطول</label>
                                        <p className="form-control-plaintext fw-medium">{clinicData.lng || 'غير محدد'}</p>
                                    </div>
                                    {clinicData.lat && clinicData.lng && (
                                        <div className="col-12">
                                            <div style={{ height: '200px', borderRadius: '8px', overflow: 'hidden', border: '2px solid #e9ecef' }}>
                                                <iframe
                                                    title="موقع العيادة"
                                                    width="100%"
                                                    height="100%"
                                                    frameBorder="0"
                                                    style={{ border: 0 }}
                                                    src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyAHQSuklfkVa-mATYvbA7odk3_LpnKG-OA&q=${parseFloat(clinicData.lat).toFixed(7)},${parseFloat(clinicData.lng).toFixed(7)}&zoom=18&language=ar`}
                                                    allowFullScreen>
                                                </iframe>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    {/* License Images */}
                    <div className="col-12 mb-4">
                        <motion.div
                            className="card shadow-lg border-0"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.5 }}
                        >
                            <div className="card-header bg-primary text-white">
                                <h5 className="mb-0">صور الترخيص</h5>
                            </div>
                            <div className="card-body p-4">
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold text-muted">الوجه الأمامي</label>
                                        {clinicData.front_license ? (
                                            <img
                                                src={clinicData.front_license}
                                                alt="الترخيص - الوجه الأمامي"
                                                className="img-fluid rounded"
                                                style={{
                                                    maxWidth: '100%',
                                                    height: '200px',
                                                    objectFit: 'cover',
                                                    border: '2px solid #e9ecef'
                                                }}
                                            />
                                        ) : (
                                            <div className="d-flex align-items-center justify-content-center" style={{ height: '200px', border: '2px dashed #e9ecef', borderRadius: '8px' }}>
                                                <span className="text-muted">لا توجد صورة</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold text-muted">الوجه الخلفي</label>
                                        {clinicData.back_license ? (
                                            <img
                                                src={clinicData.back_license}
                                                alt="الترخيص - الوجه الخلفي"
                                                className="img-fluid rounded"
                                                style={{
                                                    maxWidth: '100%',
                                                    height: '200px',
                                                    objectFit: 'cover',
                                                    border: '2px solid #e9ecef'
                                                }}
                                            />
                                        ) : (
                                            <div className="d-flex align-items-center justify-content-center" style={{ height: '200px', border: '2px dashed #e9ecef', borderRadius: '8px' }}>
                                                <span className="text-muted">لا توجد صورة</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClinicProfileView;
